// src/components/ConnectionSuccessNotification/ConnectionSuccessNotification.js
import React, { useState, useEffect, useRef } from 'react';
import { FaCheckCircle, FaTimes } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import './ConnectionSuccessNotification.css';

/**
 * ConnectionSuccessNotification Component
 * 
 * Displays a floating notification for successful WalletConnect or dApp connections.
 * Auto-dismisses after success is shown for 3 seconds.
 * 
 * @param {boolean} isVisible - Controls visibility of the notification
 * @param {string} dappName - Name of the connected dApp
 * @param {string} dappUrl - URL of the connected dApp
 * @param {function} onClose - Callback when notification is closed
 */
const ConnectionSuccessNotification = ({ 
  isVisible, 
  dappName,
  dappUrl,
  onClose 
}) => {
  const [isClosing, setIsClosing] = useState(false);
  const [progress, setProgress] = useState(0);
  const dismissTimerRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const { t } = useTranslation();
  
  // Handle visibility changes
  useEffect(() => {
    // When hiding, reset closing state
    if (!isVisible) {
      setIsClosing(false);
      setProgress(0);
    }
  }, [isVisible]);
  
  // Auto-close notification with progress bar
  useEffect(() => {
    // Clear any existing timers
    if (dismissTimerRef.current) {
      clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = null;
    }
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
    
    if (isVisible && !isClosing) {
      console.log("Connection success notification visible, scheduling auto-close");
      
      // Progress bar animation (3 seconds)
      const duration = 3000; // 3 seconds
      const intervalTime = 50; // Update every 50ms
      const increment = (intervalTime / duration) * 100;
      
      progressIntervalRef.current = setInterval(() => {
        setProgress(prev => {
          const newProgress = prev + increment;
          return newProgress >= 100 ? 100 : newProgress;
        });
      }, intervalTime);
      
      // Close after duration
      dismissTimerRef.current = setTimeout(() => {
        handleClose();
      }, duration);
    }
    
    return () => {
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    };
  }, [isVisible, isClosing]);
  
  // Handle close button click
  const handleClose = () => {
    if (isClosing) return; // Prevent multiple triggers
    
    console.log("Starting close animation");
    setIsClosing(true);
    
    // Clear any running timers
    if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    
    // Wait for animation to complete
    setTimeout(() => {
      if (onClose) {
        console.log("Animation complete, calling onClose");
        onClose();
      }
      setIsClosing(false);
      setProgress(0);
    }, 400); // Match CSS transition duration
  };
  
  if (!isVisible && !isClosing) return null;
  
  // Format URL for display
  const formattedUrl = dappUrl ? new URL(dappUrl).hostname : '';
  
  return (
    <div 
      className={`connection-notification ${isClosing ? '' : 'visible'}`}
    >
      <button 
        className="notification-close-btn"
        onClick={handleClose}
        aria-label={t('actions.close')}
      >
        <FaTimes />
      </button>
      
      <div className="notification-status-icon">
        <div className="success-icon-container">
          <FaCheckCircle className="notification-icon success" />
        </div>
      </div>
      
      <div className="notification-content">
        <div className="notification-header">
          <div className="notification-title success">
            {t('wallet.qrScanner.connected', 'Connection Successful')}
          </div>
        </div>
        
        <div className="notification-details">
          {dappName && (
            <div className="notification-dapp-name">{dappName}</div>
          )}
          {formattedUrl && (
            <div className="notification-dapp-url">{formattedUrl}</div>
          )}
        </div>
      </div>
      
      {/* Auto-dismiss progress bar */}
      <div className="progress-container">
        <div 
          className="progress-bar" 
          style={{ width: `${progress}%` }}
        ></div>
      </div>
    </div>
  );
};

export default ConnectionSuccessNotification;